import * as React from "react"
import { Head, Link, router } from "@inertiajs/react"
import { ArrowLeft, Pencil, Trash2 } from "lucide-react"
import { AdminShell } from "@/components/AdminShell"
import { PageHeader } from "@/components/PageHeader"
import { ConfirmDialog } from "@/components/flashcards/ConfirmDialog"
import { DeckEditor } from "@/components/flashcards/DeckEditor"
import { DeckFormDialog } from "@/components/flashcards/DeckFormDialog"
import { useFormat } from "@/lib/format"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import type { ContentAreaGroup, DeckDetail } from "@/types/flashcards"

export default function AdminDeckShow({
  deck,
  content_area_groups,
}: {
  deck: DeckDetail
  content_area_groups: ContentAreaGroup[]
}) {
  const { cardCount } = useFormat()
  const [editing, setEditing] = React.useState(false)
  const [deleting, setDeleting] = React.useState(false)
  const basePath = `/admin/decks/${deck.id}`
  const published = deck.status === "published"

  return (
    <>
      <Head title={`${deck.title} (official deck)`}>
        <meta name="description" content={`Manage the cards and publishing status of the official deck ${deck.title}.`} />
        <meta property="og:title" content={`${deck.title} (official deck)`} />
        <meta property="og:description" content={`Manage the cards and publishing status of the official deck ${deck.title}.`} />
      </Head>
      <AdminShell>
        <Link href="/admin/decks" className="mb-4 inline-flex items-center gap-1 text-sm">
          <ArrowLeft className="h-4 w-4" /> Official decks
        </Link>
        <PageHeader
          title={deck.title}
          description={
            <>
              <Badge tone={published ? "accent" : "muted"} className="mr-2">
                {published ? "Published" : "Draft"}
              </Badge>
              {deck.ai_draft && (
                <Badge tone="signal" className="mr-2">
                  AI draft – needs review
                </Badge>
              )}
              {cardCount(deck.card_count)}
              {deck.description ? ` · ${deck.description}` : ""}
            </>
          }
          actions={
            <>
              <Button type="button" variant="ghost" size="icon" aria-label="Edit deck" onClick={() => setEditing(true)}>
                <Pencil className="h-4 w-4" />
              </Button>
              <Button type="button" variant="ghost" size="icon" aria-label="Delete deck" onClick={() => setDeleting(true)}>
                <Trash2 className="h-4 w-4" />
              </Button>
              {deck.ai_draft && (
                <Button type="button" variant="secondary" onClick={() => router.patch(`${basePath}/mark_reviewed`)}>
                  Mark reviewed
                </Button>
              )}
              {published ? (
                <Button type="button" variant="secondary" onClick={() => router.patch(`${basePath}/unpublish`)}>
                  Unpublish
                </Button>
              ) : (
                <Button
                  type="button"
                  disabled={deck.card_count === 0 || deck.ai_draft}
                  title={deck.ai_draft ? "Mark it reviewed first" : deck.card_count === 0 ? "Add at least one card first" : undefined}
                  onClick={() => router.patch(`${basePath}/publish`)}
                >
                  Publish
                </Button>
              )}
            </>
          }
        />

        <DeckEditor deck={deck} basePath={basePath} contentAreaGroups={content_area_groups} />

        <DeckFormDialog
          open={editing}
          onOpenChange={setEditing}
          action={basePath}
          method="patch"
          title="Edit deck"
          submitLabel="Save"
          initial={{ title: deck.title, description: deck.description }}
        />
        <ConfirmDialog
          open={deleting}
          onOpenChange={setDeleting}
          title="Delete this official deck?"
          description={`"${deck.title}" and its ${cardCount(deck.card_count)} will be deleted for everyone. This can't be undone.`}
          confirmLabel="Delete deck"
          onConfirm={() => router.delete(basePath)}
        />
      </AdminShell>
    </>
  )
}
